import { useState } from 'react'
import DataTable from '../../components/DataTable'
import StatsCard from '../../components/StatsCard'
import { formatDate } from '../../utils/formatters'
import { mockArticles } from '../../data/mockData'
import { Eye, Edit2, Trash2, FileText, TrendingUp, BarChart3 } from 'lucide-react'

export default function ContentPage() {
  const [sortKey, setSortKey] = useState('createdAt')
  const [sortOrder, setSortOrder] = useState('desc')
  const [filterStatus, setFilterStatus] = useState('All')
  const [filterCategory, setFilterCategory] = useState('All')

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortOrder('asc')
    }
  }

  // Filter articles
  let filteredArticles = mockArticles
  if (filterStatus !== 'All') {
    filteredArticles = filteredArticles.filter((a) => a.status === filterStatus)
  }
  if (filterCategory !== 'All') {
    filteredArticles = filteredArticles.filter((a) => a.category === filterCategory)
  }

  const publishedCount = mockArticles.filter((a) => a.status === 'Published').length
  const draftCount = mockArticles.filter((a) => a.status === 'Draft').length
  const totalViews = mockArticles.reduce((sum, a) => sum + a.views, 0)

  // Get categories
  const categories = [...new Set(mockArticles.map((a) => a.category))]

  // Get top articles
  const topArticles = [...mockArticles].sort((a, b) => b.views - a.views).slice(0, 3)

  const columns = [
    { key: 'title', label: 'Title', sortable: true },
    { key: 'category', label: 'Category', sortable: true },
    { key: 'author', label: 'Author', sortable: true },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <span className={`px-2 py-1 rounded text-xs font-medium ${
          value === 'Published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
        }`}>{value}</span>
      ),
    },
    {
      key: 'views',
      label: 'Views',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-1">
          <Eye size={16} className="text-pawssible-slate-400" />
          <span className="font-medium">{value.toLocaleString()}</span>
        </div>
      ),
    },
    { key: 'createdAt', label: 'Created', sortable: true, render: formatDate },
    {
      key: 'id',
      label: 'Actions',
      render: () => (
        <div className="flex gap-2">
          <button className="p-1 hover:bg-pawssible-slate-100 rounded transition-colors">
            <Edit2 size={16} className="text-pawssible-teal-600" />
          </button>
          <button className="p-1 hover:bg-red-50 rounded transition-colors">
            <Trash2 size={16} className="text-red-600" />
          </button>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-pawssible-slate-900">Content Management</h1>
        <p className="text-pawssible-slate-600 mt-1">Create and manage health articles & educational content</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard
          label="Total Articles"
          value={mockArticles.length}
          icon={FileText}
          color="teal"
        />
        <StatsCard
          label="Published"
          value={publishedCount}
          icon={TrendingUp}
          color="amber"
        />
        <StatsCard
          label="Drafts"
          value={draftCount}
          icon={BarChart3}
          color="teal"
        />
        <StatsCard
          label="Total Views"
          value={totalViews.toLocaleString()}
          icon={Eye}
          color="amber"
        />
      </div>

      {/* Top Articles */}
      <div className="card">
        <h3 className="text-lg font-semibold text-pawssible-slate-900 mb-4">Most Viewed Articles</h3>
        <div className="space-y-3">
          {topArticles.map((article, i) => (
            <div key={article.id} className="flex items-center justify-between border-b border-pawssible-slate-100 pb-3 last:border-b-0">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg font-bold text-pawssible-teal-600">#{i + 1}</span>
                  <p className="font-medium text-pawssible-slate-900">{article.title}</p>
                </div>
                <div className="flex gap-4 text-xs text-pawssible-slate-500">
                  <span>{article.category}</span>
                  <span>by {article.author}</span>
                </div>
              </div>
              <div className="text-right">
                <p className="font-semibold text-pawssible-slate-900">{article.views.toLocaleString()}</p>
                <p className="text-xs text-pawssible-slate-500">views</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Filter Controls */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">Filter by Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input"
            >
              <option>All</option>
              <option>Published</option>
              <option>Draft</option>
            </select>
          </div>
          <div>
            <label className="label">Filter by Category</label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="input"
            >
              <option>All</option>
              {categories.map((cat) => (
                <option key={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 flex-wrap">
        <button className="btn-primary">Create Article</button>
        <button className="btn-secondary">Bulk Publish</button>
        <button className="btn-secondary">View Analytics</button>
      </div>

      {/* Articles Table */}
      <div>
        <h2 className="text-xl font-semibold text-pawssible-slate-900 mb-4">All Articles</h2>
        <DataTable
          columns={columns}
          data={filteredArticles}
          onSort={handleSort}
          sortKey={sortKey}
          sortOrder={sortOrder}
          pageSize={15}
        />
      </div>
    </div>
  )
}
